import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  CloseButton,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import {
  addGoalItem,
  getGoalCategories,
} from '../../stores/slices/goals/goalsActions';
import * as chrono from 'chrono-node';
import { saveReminder } from '../../stores/slices/reminders/remindersActions';

const schema = yup
  .object({
    goalItem: yup.string().required('Description is required'),
  })
  .required();

const AddGoal = ({ category }) => {
  const [reminderIsSaving, setReminderIsSaving] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { userInfo } = useSelector(state => state.user);
  const dispatch = useDispatch();
  const toast = useToast();
  const toastIdRef = useRef();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const saveReminderData = async data => {
    setReminderIsSaving(true);
    toast.closeAll();
    toast({
      description: 'Saving reminder',
      icon: <Spinner />,
      colorScheme: 'primary',
      status: 'success',
      duration: null,
      isClosable: false,
      position: 'bottom-right',
      variant: 'primary',
    });
    const res = await dispatch(
      saveReminder({
        reminderDetail: data.text,
        userId: userInfo.id,
        due: new Date(data.parsedData[0].start.date()).toISOString(),
        userSelect: [
          {
            value: userInfo.id,
          },
        ],
        isUpdate: { state: false, reminder: null },
        goalId: data.goalId,
      })
    );
    if (res.type === 'reminders/saveReminder/rejected') {
      toast({
        title: 'Something went wrong',
        description: 'Please try again later.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } else {
      toast.closeAll();
      toast({
        description: 'Reminder has been created',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
      setReminderIsSaving(false);
    }
  };

  const addReminderPrompt = data => {
    toast.closeAll();
    toastIdRef.current = toast({
      description: 'Item has been added',
      colorScheme: 'primary',
      status: 'info',
      duration: null,
      isClosable: true,
      position: 'bottom-right',
      variant: 'primary',
      render: () => (
        <Alert
          status="success"
          variant={'primary'}
          borderRadius={'8'}
          justifyContent={'space-between'}
        >
          <HStack>
            <AlertIcon alignSelf="flex-start" />
            <VStack align={'start'}>
              <AlertTitle>Item has been added</AlertTitle>
              <AlertDescription>Add reminder?</AlertDescription>
              <Button
                border={'2px solid'}
                _hover={{ color: 'primary', backgroundColor: 'white' }}
                variant={'outline'}
                size={'sm'}
                isLoading={reminderIsSaving}
                onClick={() => saveReminderData(data)}
              >
                Add
              </Button>
            </VStack>
          </HStack>

          <CloseButton
            alignSelf="flex-start"
            position="relative"
            right={-1}
            top={-1}
            onClick={() => {
              if (toastIdRef.current) {
                toast.close(toastIdRef.current);
              }
            }}
          />
        </Alert>
      ),
    });
  };
  const onSaveItem = async data => {
    const parsedData = chrono.parse(data.goalItem, Date.now(), {
      forwardDate: true,
    });

    const res = await dispatch(
      addGoalItem({
        ...data,
        goalCategoryId: category.id,
        userId: userInfo.id,
      })
    );

    await dispatch(getGoalCategories());
    reset();
    onClose();
    if (parsedData.length > 0) {
      addReminderPrompt({
        text: data.goalItem,
        parsedData,
        goalId: res.payload.data.id,
      });
    } else {
      toast({
        description: 'Item has been added',
        colorScheme: 'primary',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
        variant: 'primary',
      });
    }
  };

  return (
    <React.Fragment>
      <Tooltip label="Add item" aria-label="Add item">
        <IconButton
          variant={'outline'}
          size="xs"
          border={'1.5px solid'}
          borderColor={'primary'}
          p={0}
          color="primary"
          icon={<FaPlus />}
          onClick={onOpen}
        />
      </Tooltip>

      <Modal
        // initialFocusRef={initialRef}
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <form onSubmit={handleSubmit(onSaveItem)}>
            <ModalHeader>Add an Item</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <FormControl id="goalItem" isInvalid={errors.goalItem}>
                <FormLabel>Item Description</FormLabel>
                <Textarea id="goalItem" {...register('goalItem')} autoFocus rows={'4'}/>
                <FormErrorMessage>
                  {errors.goalItem && errors.goalItem.message}
                </FormErrorMessage>
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button mr={3} type="submit" isLoading={isSubmitting}>
                Save
              </Button>
              <Button
                onClick={onClose}
                variant="outline"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </React.Fragment>
  );
};

export default AddGoal;
